/**
 * Local SQLite adapters for project snapshots.
 * Mirrors the Convex api.projectSnapshots.* interface.
 */
import { localDb, newId, now } from "@/lib/localDb";
import { invalidate } from "./queryCache";
import { useLocalQuery, useLocalGet } from "./useLocalQuery";

// ─── Types ────────────────────────────────────────────────────────────────────

export type SnapshotTrigger = "manual" | "auto" | "pre_publish" | "pre_bulk_delete";

export interface SnapshotData {
  schemaVersion: number;
  capturedAt: string;
  project?: {
    name?: string;
    description?: string;
    data?: unknown;
  };
  panoramas?: Array<{
    id: string;
    name: string;
    order?: number;
    data?: unknown;
    gps_location?: unknown;
    current_version_id?: string;
  }>;
  hotspots?: Array<{
    id: string;
    panorama_id: string;
    name?: string;
    type?: string;
    content?: unknown;
    position?: unknown;
    rotation?: unknown;
    scale?: unknown;
    visible?: boolean;
    order?: number;
  }>;
  variables?: {
    projectVariables?: unknown;
    craftspaceVariables?: unknown;
  };
}

export interface LocalProjectSnapshot {
  _id: string;
  project_id: string;
  user_id?: string;
  name?: string;
  trigger: SnapshotTrigger;
  snapshot_data: SnapshotData;
  size_bytes?: number;
  created_at?: string;
  updated_at?: string;
}

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useSnapshotsByProject(
  projectId: string | undefined,
  craftspaceId: string | undefined,
): LocalProjectSnapshot[] | undefined {
  return useLocalQuery<LocalProjectSnapshot>(
    `SELECT id as _id, id, project_id, user_id, name, "trigger",
       snapshot_data, size_bytes, created_at, updated_at
     FROM project_snapshots
     WHERE project_id = ?
     ORDER BY created_at DESC`,
    [projectId ?? ""],
    ["project_snapshots"],
    ["snapshot_data"],
    craftspaceId,
  );
}

export function useSnapshot(
  id: string | undefined,
  craftspaceId: string | undefined,
): LocalProjectSnapshot | null | undefined {
  return useLocalGet<LocalProjectSnapshot>(
    `SELECT id as _id, id, project_id, user_id, name, "trigger",
       snapshot_data, size_bytes, created_at, updated_at
     FROM project_snapshots
     WHERE id = ?`,
    [id ?? ""],
    ["project_snapshots"],
    ["snapshot_data"],
    craftspaceId,
  );
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export async function createSnapshot(params: {
  craftspace_id: string;
  project_id: string;
  name?: string;
  trigger: SnapshotTrigger;
  snapshot_data: SnapshotData;
}): Promise<string> {
  const id = newId();
  const ts = now();
  const dataJson = JSON.stringify(params.snapshot_data);
  const sizeBytes = new TextEncoder().encode(dataJson).length;

  await localDb(params.craftspace_id).run(
    `INSERT INTO project_snapshots (id, project_id, user_id, name, "trigger",
       snapshot_data, size_bytes, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      id,
      params.project_id,
      null, // user_id not available in local mode
      params.name ?? null,
      params.trigger,
      dataJson,
      sizeBytes,
      ts,
      ts,
    ],
  );

  invalidate("project_snapshots");
  return id;
}

export async function removeSnapshot(id: string, craftspaceId: string): Promise<void> {
  await localDb(craftspaceId).run(`DELETE FROM project_snapshots WHERE id = ?`, [id]);
  invalidate("project_snapshots");
}

export async function restoreSnapshot(id: string, craftspaceId: string): Promise<void> {
  const row = await localDb(craftspaceId).get<{ snapshot_data: string; project_id: string }>(
    `SELECT snapshot_data, project_id FROM project_snapshots WHERE id = ?`,
    [id],
  );

  if (!row) throw new Error("Snapshot not found");

  const data: SnapshotData =
    typeof row.snapshot_data === "string"
      ? JSON.parse(row.snapshot_data)
      : (row.snapshot_data as SnapshotData);

  const ts = now();
  const operations: Array<{ sql: string; params?: unknown[] }> = [];

  // Restore project metadata
  if (data.project) {
    const sets: string[] = ["updated_at = ?"];
    const vals: unknown[] = [ts];

    if (data.project.name) { sets.push("name = ?"); vals.push(data.project.name); }
    if (data.project.description !== undefined) { sets.push("description = ?"); vals.push(data.project.description); }
    if (data.project.data !== undefined) { sets.push("data = ?"); vals.push(JSON.stringify(data.project.data)); }

    vals.push(row.project_id);
    operations.push({
      sql: `UPDATE projects SET ${sets.join(", ")} WHERE id = ?`,
      params: vals,
    });
  }

  // Restore hotspots: delete current, re-insert from snapshot
  if (data.hotspots) {
    operations.push({
      sql: `DELETE FROM hotspots WHERE project_id = ?`,
      params: [row.project_id],
    });

    for (const hotspot of data.hotspots) {
      const hId = newId();
      operations.push({
        sql: `INSERT INTO hotspots (id, project_id, panorama_id, type, label,
                position_x, position_y, position_z,
                rotation_x, rotation_y, rotation_z,
                scale_x, scale_y, scale_z,
                visible, target_panorama_id, content, style, "order",
                created_at, updated_at)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, '{}', ?, ?, ?)`,
        params: [
          hId,
          row.project_id,
          hotspot.panorama_id,
          hotspot.type ?? "navigation",
          hotspot.name ?? null,
          (hotspot.position as any)?.x ?? 0,
          (hotspot.position as any)?.y ?? 0,
          (hotspot.position as any)?.z ?? 0,
          (hotspot.rotation as any)?.x ?? 0,
          (hotspot.rotation as any)?.y ?? 0,
          (hotspot.rotation as any)?.z ?? 0,
          (hotspot.scale as any)?.x ?? 1,
          (hotspot.scale as any)?.y ?? 1,
          (hotspot.scale as any)?.z ?? 1,
          hotspot.visible !== false ? 1 : 0,
          (hotspot.content as any)?.targetPanoramaId ?? null,
          hotspot.content ? JSON.stringify(hotspot.content) : "{}",
          hotspot.order ?? 0,
          ts,
          ts,
        ],
      });
    }
  }

  // Restore panorama metadata (not file versions)
  if (data.panoramas) {
    for (const pan of data.panoramas) {
      const sets: string[] = ["updated_at = ?"];
      const vals: unknown[] = [ts];

      if (pan.name) { sets.push("name = ?"); vals.push(pan.name); }
      if (pan.order !== undefined) { sets.push("order_index = ?"); vals.push(pan.order); }
      if (pan.data !== undefined) { sets.push("metadata = ?"); vals.push(JSON.stringify(pan.data)); }

      vals.push(pan.id);
      operations.push({
        sql: `UPDATE panoramas SET ${sets.join(", ")} WHERE id = ?`,
        params: vals,
      });
    }
  }

  if (operations.length > 0) {
    await localDb(craftspaceId).transaction(operations);
  }

  invalidate("projects", "panoramas", "hotspots", "project_snapshots");
}

/**
 * Collect current project state into a SnapshotData object.
 * Call this before passing to createSnapshot.
 */
export async function collectSnapshotData(projectId: string, craftspaceId: string): Promise<SnapshotData> {
  const project = await localDb(craftspaceId).get<{ name: string; description: string; data: string }>(
    `SELECT name, description, data FROM projects WHERE id = ?`,
    [projectId],
  );

  const panoramas = await localDb(craftspaceId).query<{
    id: string; name: string; order_index: number; metadata: string;
  }>(
    `SELECT id, name, order_index, metadata FROM panoramas
     WHERE project_id = ? AND (status = 'active' OR status IS NULL)`,
    [projectId],
  );

  const hotspots = await localDb(craftspaceId).query<{
    id: string; panorama_id: string; label: string; type: string;
    position_x: number; position_y: number; position_z: number;
    rotation_x: number; rotation_y: number; rotation_z: number;
    scale_x: number; scale_y: number; scale_z: number;
    visible: number; content: string; order: number;
  }>(
    `SELECT id, panorama_id, label, type,
       position_x, position_y, position_z,
       rotation_x, rotation_y, rotation_z,
       scale_x, scale_y, scale_z,
       visible, content, "order"
     FROM hotspots WHERE project_id = ?`,
    [projectId],
  );

  return {
    schemaVersion: 1,
    capturedAt: now(),
    project: project
      ? {
          name: project.name,
          description: project.description,
          data: project.data ? JSON.parse(project.data) : undefined,
        }
      : undefined,
    panoramas: panoramas.map((p) => ({
      id: p.id,
      name: p.name,
      order: p.order_index,
      data: p.metadata ? JSON.parse(p.metadata) : undefined,
    })),
    hotspots: hotspots.map((h) => ({
      id: h.id,
      panorama_id: h.panorama_id,
      name: h.label,
      type: h.type,
      content: h.content ? JSON.parse(h.content) : undefined,
      position: { x: h.position_x, y: h.position_y, z: h.position_z },
      rotation: { x: h.rotation_x, y: h.rotation_y, z: h.rotation_z },
      scale: { x: h.scale_x, y: h.scale_y, z: h.scale_z },
      visible: h.visible !== 0,
      order: h.order,
    })),
  };
}
